<script setup>
/* global axios */
// Box "Kanban" na lateral da conversa — associa o ticket a um card do Kanban (nativo,
// substitui a injeção via DOM do projeto antigo).
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useKanbanStageBadge } from 'dashboard/composables/useKanbanStageBadge';
import KanbanSelect from 'dashboard/routes/dashboard/kanban/KanbanSelect.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});
const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const card = ref(null);
const loading = ref(true);
const funnels = ref([]);
const selFunnel = ref(null);
const selStage = ref(null);
const adding = ref(false);

const { setStage, clearStage } = useKanbanStageBadge();

const stages = computed(
  () => funnels.value.find(f => f.id === selFunnel.value)?.stages || []
);
const funnelOptions = computed(() =>
  funnels.value.map(f => ({ value: f.id, label: f.name }))
);
const stageOptions = computed(() =>
  stages.value.map(s => ({ value: s.id, label: s.name }))
);

// Atualiza a tag de funil/etapa do listing de conversas localmente (sem refetch)
// quando a etapa muda aqui — senão a tag fica presa no cache de 60s.
const syncBadge = () => {
  const f = funnels.value.find(x => x.id === selFunnel.value);
  const s = f?.stages?.find(x => x.id === selStage.value);
  if (f && s) {
    setStage(props.conversationId, {
      funnel_name: f.name,
      funnel_color: f.color,
      stage_name: s.name,
      stage_color: s.color,
    });
  }
};
const leadLabel = s =>
  ({
    won: t('KANBAN.card.leadWonLabel'),
    lost: t('KANBAN.card.leadLostLabel'),
  })[s] || t('KANBAN.card.leadOpenLabel');

const moving = ref(false);
const setAddDefaults = () => {
  if (funnels.value[0]) {
    selFunnel.value = funnels.value[0].id;
    selStage.value = funnels.value[0].stages[0]?.id;
  }
};
const loadFunnels = async () => {
  try {
    funnels.value = (await axios.get(`${base}/funnels`)).data.data || [];
  } catch (e) {
    /* noop */
  }
};
const loadCard = async () => {
  loading.value = true;
  try {
    card.value = (
      await axios.get(`${base}/cards/for_conversation`, {
        params: { conversation_id: props.conversationId },
      })
    ).data.data;
  } catch (e) {
    card.value = null;
  }
  if (card.value) {
    selFunnel.value = card.value.funnel_id;
    selStage.value = card.value.stage_id;
  } else {
    setAddDefaults();
  }
  loading.value = false;
};
const addToKanban = async () => {
  if (!selStage.value) return;
  adding.value = true;
  try {
    await axios.post(`${base}/cards`, {
      conversation_id: props.conversationId,
      stage_id: selStage.value,
    });
    await loadCard();
    syncBadge();
  } finally {
    adding.value = false;
  }
};
const moveCard = async () => {
  if (!card.value || !selStage.value) return;
  moving.value = true;
  try {
    await axios.patch(`${base}/cards/${card.value.id}/move`, {
      stage_id: selStage.value,
      position: 0,
    });
    await loadCard();
    syncBadge();
  } finally {
    moving.value = false;
  }
};
const onFunnelChange = () => {
  selStage.value = stages.value[0]?.id;
  if (card.value) moveCard();
};
const onStageChange = () => {
  if (card.value) moveCard();
};
const removeFromKanban = async () => {
  if (!card.value || !window.confirm(t('KANBAN.box.removeConfirm'))) return;
  await axios.delete(`${base}/cards/${card.value.id}`);
  clearStage(props.conversationId);
  card.value = null;
  setAddDefaults();
};
const openCard = () =>
  card.value &&
  router.push(`/app/accounts/${accountId}/kanban/cards/${card.value.id}`);

watch(
  () => props.conversationId,
  () => {
    loadCard();
  }
);
onMounted(async () => {
  await loadFunnels();
  await loadCard();
});
</script>

<template>
  <div class="flex flex-col gap-2.5 px-3.5 pt-2.5 pb-3.5">
    <div v-if="loading" class="text-xs text-n-slate-10">
      {{ t('KANBAN.loading') }}
    </div>
    <template v-else-if="card">
      <label class="flex flex-col gap-1 text-sm"
        ><span class="text-xs text-n-slate-10">{{ t('KANBAN.funnel') }}</span>
        <KanbanSelect
          v-model="selFunnel"
          :options="funnelOptions"
          @change="onFunnelChange"
        />
      </label>
      <label class="flex flex-col gap-1 text-sm"
        ><span class="text-xs text-n-slate-10">{{
          t('KANBAN.box.stage')
        }}</span>
        <KanbanSelect
          v-model="selStage"
          :options="stageOptions"
          :disabled="moving"
          @change="onStageChange"
        />
      </label>
      <div class="flex items-center justify-between text-sm">
        <span class="text-xs text-n-slate-10">{{ t('KANBAN.box.status') }}</span
        ><span
          class="px-2 py-0.5 text-[0.6875rem] font-medium rounded-full"
          :class="{
            'bg-gray-100 text-gray-600 dark:bg-n-solid-3 dark:text-n-slate-11':
              (card.lead_status || 'open') === 'open',
            'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300':
              card.lead_status === 'won',
            'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300':
              card.lead_status === 'lost',
          }"
          >{{ leadLabel(card.lead_status) }}</span
        >
      </div>
      <div class="flex gap-1.5">
        <button
          class="flex-1 px-3 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95"
          @click="openCard"
        >
          {{ t('KANBAN.box.openCard') }}
        </button>
        <button
          class="px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-transparent hover:bg-n-alpha-1"
          :title="t('KANBAN.box.removeFromKanban')"
          @click="removeFromKanban"
        >
          <svg
            class="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
            />
          </svg>
        </button>
      </div>
    </template>
    <template v-else>
      <p class="text-xs text-n-slate-10">{{ t('KANBAN.box.notInKanban') }}</p>
      <KanbanSelect
        v-model="selFunnel"
        :options="funnelOptions"
        @change="onFunnelChange"
      />
      <KanbanSelect v-model="selStage" :options="stageOptions" />
      <button
        class="w-full px-3 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="adding || !selStage"
        @click="addToKanban"
      >
        {{ adding ? t('KANBAN.box.adding') : t('KANBAN.box.addToKanban') }}
      </button>
    </template>
  </div>
</template>
